import React from 'react';
import { Divider } from '@material-ui/core';
import DrcWrapper from '../../../components/DrcWrapper';
import PrimeReactTable from '../../../components/PrimeReactTable';
import Stepper from '../../../components/Stepper';
import { withStyles } from '@material-ui/core/styles';
import { DrcSelect, DrcTranslate, DrcButton, DrcDatePicker, DrcTooltip, DrcDialog } from '@driscollsinc/driscolls-react-components';
import GridStyles from '../../../styles/gridStyles';
import { showLoadingScreenAction, hideLoadingScreenAction, showToast, setPageTitleAction } from '../../../actions/actions';
import { connect } from 'react-redux';
import { withOktaAuth } from '@okta/okta-react';
import { displayErrorMessage, getAppliedFilters, makeColumns, makeOptions, userBusinessUnit } from '../../../utils/helper';
import ActionsFormatter from '../../../components/cellFormatters/ActionFormatter';
import MasterDataUtilities from '../../../data/MasterDataUtilities';
import { Call } from '@driscollsinc/driscolls-display-rules';
import APIEndPoints from '../../../services/api';
import { DuAuthenticationUtilities } from '@driscollsinc/driscolls-react-utilities';
import { decimal, maxValue, precision, alphaNumeric } from '../../../utils/validations';
import GGSFormInput from '../../../components/GGSFormInput';
import Form from 'react-validation/build/form';
import { settlementRateDsColumns, convertDateToIso, commodityOptions, LOOKUP_FIELDS } from '../../../data/constants';
import FilterListSharp from '@material-ui/icons/FilterListSharp';
import Filter from '../../../components/Filter/Filter';
import { ReactComponent as Add } from '../../../Images/SVG/Add_Enabled.svg';

const pageTitle = 'Settlement Rate';
const styles = (theme) => ({
    input: {
        '& .MuiOutlinedInput-input': {
            textAlign: 'center'
        }
    },
    gridStyles: GridStyles.styles(theme, '300px', '300px'),
    tableContainer: {
        margin: theme.spacing(0, 3)
    },
    root: {
        '& .select': {
            top: 2
        },
        margin: '-5px 0px 0px'
    },
    filterContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    pageActions: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between'
    }
});

class SettlementRate extends React.Component {
    state = {
        data: [],
        pageNum: 0,
        selectedRow: [],
        pageSize: 50,
        totalCount: 0,
        columns: [],
        rowData: null,
        editingRow: {},
        showAddNew: false,
        newRecord: {},
        invalidFields: [],
        dateError: ''
    };
    excludingFields = ['Actions', 'SettlementRateID'];
    readOnly = false;
    uniqueKeyField = 'SettlementRateID';
    widthIsSufficient = window.innerWidth > 955;
    widthIsInSufficient = false;

    validationFields = {
        CommissionRate: {
            key: 'CommissionRate',
            validations: [decimal, maxValue, precision],
            customProps: { maxValue: 100, precision: 2 }
        },
        StartDateActive: {
            key: 'StartDateActive',
            validations: [alphaNumeric],
            customProps: {}
        },
        EndDateActive: {
            key: 'EndDateActive',
            validations: [alphaNumeric],
            customProps: {}
        },
        ApplicationFullName: {
            key: 'ApplicationFullName',
            validations: [alphaNumeric],
            customProps: {}
        },
        Application: {
            key: 'ApplicationNetSuite',
            validations: [alphaNumeric],
            customProps: {}
        },
        CoolerName: {
            key: 'CoolerName',
            validations: [alphaNumeric],
            customProps: {}
        },
        LocationCode: {
            key: 'LocationCode',
            validations: [alphaNumeric],
            customProps: {}
        }
    };

    componentDidMount() {
        this.loadData();
        window.addEventListener('resize', this.updateDataTable);
        if (this.props.pageTitle !== pageTitle) {
            this.props.setPageTitle(pageTitle);
        }
    }
    componentWillUnmount() {
        window.removeEventListener('resize', this.updateDataTable);
    }

    componentDidUpdate(_prevProps, prevState) {
        if (
            this.state.editingRow.StartDateActive !== prevState.editingRow.StartDateActive ||
            this.state.editingRow.EndDateActive !== prevState.editingRow.EndDateActive
        )
            this.checkDateValidation();
    }

    checkDateValidation() {
        if (
            new Date(this.state.editingRow.StartDateActive) > new Date(this.state.editingRow.EndDateActive) &&
            this.state.editingRow.EndDateActive !== null
        ) {
            if (!this.state.dateError)
                return this.setState({
                    dateError: <DrcTranslate>{'End Date should be greater than start date'}</DrcTranslate>,
                    invalidFields: ['EndDateActive']
                });
        } else {
            if (this.state.dateError) return this.setState({ dateError: '', invalidFields: [] });
        }
    }

    editData = (_event, data, _index) => {
        this.setState({ editingRow: { ...data, [data[this.uniqueKeyField]]: true }, invalidFields: [] });
    };
    cancelEdit = (_event, _rowData) => {
        this.setState({ editingRow: {}, invalidFields: [] });
    };
    cannotEdit = (_row) => !(this.props.isUserAdmin && this.props.selectedRole.includes('-Admin'));
    deleteData = async (_rowData) => !this.props.isUserAdmin;
    handleValidationDetected = (validate, fieldName) => {
        if (validate === undefined) {
            return;
        }
        if (validate) {
            if (!this.state.invalidFields.includes(fieldName)) {
                let newFields = [...this.state.invalidFields];
                newFields.push(fieldName);
                this.setState({ invalidFields: newFields });
            }
        } else {
            if (this.state.invalidFields.includes(fieldName))
                this.setState((prevState) => ({ ...prevState, invalidFields: prevState.invalidFields.filter((field) => field !== fieldName) }));
        }
    };
    confirmEdit = async (_event, _rowData) => {
        this.props.showLoadingScreenAction(<DrcTranslate>{'Loading data'}</DrcTranslate>);
        let token = await this.props.oktaAuth.getAccessToken();
        let loggedInUser = DuAuthenticationUtilities.GetEmail(token);
        try {
            let data = await Call(
                {
                    name: 'SettlementRate',
                    url: APIEndPoints.PUT_SETTLEMENT_RATE(),
                    method: 'PUT',
                    options: {},
                    data: {
                        SettlementRateID: this.state.editingRow[this.uniqueKeyField],
                        Commodity: this.state.editingRow['Commodity'],
                        CoolerName: this.state.editingRow['CoolerName'],
                        ApplicationNetSuite: this.state.editingRow['ApplicationNetSuite'],
                        LocationCode: this.state.editingRow['LocationCode'],
                        BusinessUnit: this.state.editingRow['BusinessUnit'],
                        ApplicationFullName: this.state.editingRow['ApplicationFullName'],
                        CommissionRate: '' + (this.state.editingRow['CommissionRate'] || 0),
                        EndDateActive: this.state.editingRow['EndDateActive'] ? convertDateToIso(this.state.editingRow['EndDateActive']) : '',
                        StartDateActive: this.state.editingRow['StartDateActive'] ? convertDateToIso(this.state.editingRow['StartDateActive']) : '',
                        ModifiedBy: loggedInUser,
                        ModifiedDateTime: new Date().toISOString()
                    }
                },
                token
            );
            if (!data) {
                this.showError();
            } else if ((data.display.Message || '').includes('start date and end date overlap')) {
                this.setState({ editingRow: {} });
                return this.showError(data.display.Message, 'Start date and End date overlap');
            } else {
                this.props.hideLoadingScreenAction();
                this.setState({ editingRow: {} });
                this.loadData();
                this.props.showToast(<DrcTranslate>{'Updated Successfully'}</DrcTranslate>, true);
            }
        } catch (error) {
            this.setState({ editingRow: {} });
            this.showError(error);
        }
    };

    addNewRecord = async () => {
        try {
            this.props.showLoadingScreenAction('Adding data');
            let token = await this.props.oktaAuth.getAccessToken();
            let loggedInUser = DuAuthenticationUtilities.GetEmail(token);
            let data = await Call(
                {
                    name: 'SettlementRate',
                    url: APIEndPoints.PUT_SETTLEMENT_RATE(),
                    method: 'POST',
                    options: {},
                    data: {
                        BusinessUnit: userBusinessUnit(),
                        Commodity: this.state.newRecord['Commodity'].value,
                        LocationCode: this.state.newRecord['LocationCode'],
                        CoolerName: this.state.newRecord['CoolerName'].value,
                        ApplicationNetSuite: this.state.newRecord['Application'] || '',
                        ApplicationFullName: null,
                        CommissionRate: '' + this.state.newRecord['CommissionRate'],
                        StartDateActive: convertDateToIso(this.state.newRecord['StartDateActive']),
                        EndDateActive: this.state.newRecord['EndDateActive'] ? convertDateToIso(this.state.newRecord['EndDateActive']) : '',
                        CreatedBy: loggedInUser,
                        ModifiedDateTime: new Date().toISOString(),
                        ModifiedBy: loggedInUser,
                        CreatedDateTime: new Date().toISOString()
                    }
                },
                token
            );
            if (data.errors.length && !data.raw) {
                this.setState({ showAddNew: false, newRecord: {} });
                return this.showError(data.errors[0]);
            } else if ((data.display.Message || '').includes('start date and end date overlap')) {
                this.setState({ showAddNew: false, newRecord: {} });
                return this.showError(data.display.Message, 'Start date and End date overlap');
            } else if ((data.display.Message || '').includes('Duplicate entry')) {
                this.setState({ showAddNew: false, newRecord: {} });
                return this.showError(data.display.Message, 'Duplicate entry');
            } else {
                this.props.hideLoadingScreenAction();
                this.setState({ showAddNew: false, newRecord: {} });
                this.props.showToast(<DrcTranslate>{'Added Successfully'}</DrcTranslate>, true);
                this.loadData();
            }
        } catch (error) {
            this.showError(error);
        }
    };

    cannotDelete = (_row) => null;

    getActionsCell = (row) => {
        return (
            <ActionsFormatter
                rowEdit={true}
                uniqueKeyField={this.uniqueKeyField}
                objState={this}
                records={this.state.data}
                initRowEdit={this.editData}
                confirmEdit={this.confirmEdit}
                cancelEdit={this.cancelEdit}
                cannotDelete={this.cannotDelete}
                cannotEdit={this.cannotEdit}
                deleteAttribute={this.deleteData}
                row={row}
                checkRowValidation={() => !!this.state.invalidFields.length}
                rowUnderEditUniqueKeyValue={row[this.uniqueKeyField]}
            />
        );
    };

    updateTableData = (response, hasFrozenColumns = true) => {
        let data = response?.display?.Data || [];
        let columns = data.length
            ? makeColumns(
                  Object.keys(data[0]),
                  this.excludingFields,
                  this.getActionsCell,
                  true,
                  ['CommissionRate', 'EndDateActive', 'StartDateActive'],
                  [],
                  settlementRateDsColumns
              )
            : [];
        this.setState({
            data,
            totalCount: response?.raw?.data?.TotalCount || 0,
            columns,
            unfreeze: !hasFrozenColumns
        });
    };
    updateDataTable = (_event, force = false) => {
        if (window.innerWidth > 955 && !this.widthIsSufficient) {
            this.widthIsSufficient = true;
            this.widthIsInSufficient = false;
            this.updateTableData(this.response || [], true);
        } else if (window.innerWidth <= 955 && !this.widthIsInSufficient) {
            this.widthIsInSufficient = true;
            this.widthIsSufficient = false;
            this.updateTableData(this.response || [], false);
        } else if (force) {
            this.updateTableData(this.response || [], true);
        }
    };
    showError = (err, msg) => {
        this.props.hideLoadingScreenAction();
        this.props.showToast(<DrcTranslate>{displayErrorMessage(msg)}</DrcTranslate>, false);
        console.error('error : ', err);
    };
    loadData = async () => {
        this.props.showLoadingScreenAction(<DrcTranslate>{'Loading data'}</DrcTranslate>);
        let token = await this.props.oktaAuth.getAccessToken();
        try {
            let data = await Call(
                {
                    name: 'SettlementRate',
                    url: APIEndPoints.GET_SETTLEMENT_RATE(
                        this.state.pageNum / this.state.pageSize + 1,
                        this.state.pageSize,
                        userBusinessUnit(),
                        getAppliedFilters(this.props.filters)
                    ),
                    method: 'GET',
                    options: {}
                },
                token
            );
            if (data.errors.length && !data.raw) {
                return this.showError(data.errors[0]);
            }
            this.response = data;
            this.updateDataTable(null, true);
            this.props.hideLoadingScreenAction();
        } catch (error) {
            this.showError(error);
        }
    };
    getRowEditor = (key, col, others) => {
        switch (key) {
            case 'CommissionRate':
                let field = this.validationFields[key] ?? {};
                return this.inputTextEditor(key, col, others, field.customProps, field.validations);
            case 'EndDateActive':
                return this.dateCellEditor(key, true, true);
            case 'StartDateActive':
                return this.dateCellEditor(key, true, false);
        }
    };

    exceedsTwoDecimals = (value) => /\.\d{3,}/.test(String(value ?? ''));

    inputTextEditor = (key, _col, _others, customProps, validations = []) => {
        return (
            <Form>
                <GGSFormInput
                    className={this.props.classes.input}
                    onChange={(e) => {
                        if (this.exceedsTwoDecimals(e.target.value)) return;
                        return this.setState(() => ({
                            editingRow: { ...this.state.editingRow, [key]: e.target.value }
                        }));
                    }}
                    value={this.state.editingRow[key]}
                    validations={validations}
                    {...customProps}
                    maxvalue={100}
                    validationDetected={(validate) => this.handleValidationDetected(validate, key)}
                />
            </Form>
        );
    };
    dateCellEditor = (key, clearable, errorMsg) => {
        return (
            <>
                <DrcDatePicker
                    clearable={clearable}
                    emptyLabel=" "
                    InputProps={{ readOnly: true }}
                    style={{ marginTop: `${errorMsg && this.state.dateError ? '19px' : '0'}` }}
                    selectedDate={this.state.editingRow[key]}
                    onChange={(e) => this.setState((prevState) => ({ editingRow: { ...prevState.editingRow, [key]: e } }))}
                />
                {errorMsg && this.state.dateError && (
                    <DrcTooltip tipText={this.state.dateError}>
                        <p style={{ color: 'red', fontSize: '10px', marginTop: '-5px', textOverflow: 'ellipsis' }}>{this.state.dateError || ''}</p>
                    </DrcTooltip>
                )}
            </>
        );
    };

    onPage = (event) => {
        this.setState({ pageNum: event.first });
        this.loadData();
    };

    applyFilter = () => {
        this.setState({ pageNum: 0 }, () => this.loadData());
    };

    toggleShowFilter = () => this.setState({ showFilter: !this.state.showFilter });

    resetFilter = () => {
        this.setState({ pageNum: 0 }, () => this.loadData());
    };

    render() {
        const { classes } = this.props;
        if (!MasterDataUtilities.Check(this.props.isMasterDataInitialized)) {
            return MasterDataUtilities.Redirect();
        }

        return (
            <DrcWrapper>
                <Stepper page={pageTitle} isDataSetup />
                <Divider />
                <div className={classes.filterContainer}>
                    <Filter
                        showFilter={this.state.showFilter}
                        applyFilter={this.applyFilter}
                        resetFilter={this.resetFilter}
                        filterNames={['Commodity', 'LocationCode', 'CoolerName', 'Application']}
                    />
                </div>
                <div className={classes.pageActions}>
                    <div>
                        <DrcButton onClick={this.toggleShowFilter}>
                            <FilterListSharp className={classes.icon} />
                            <DrcTranslate>{'Filter'}</DrcTranslate>
                        </DrcButton>
                    </div>
                    <DrcButton
                        onClick={(_event) => {
                            this.setState({ showAddNew: true });
                        }}
                        noStyle
                        title="Add"
                        disabled={!(this.props.isUserAdmin && this.props.selectedRole.includes('-Admin'))}
                    >
                        <Add style={{ width: '50px', height: '20px', marginRight: '-10px' }} />
                        <DrcTranslate>{'Add'}</DrcTranslate>
                    </DrcButton>
                </div>
                <div className={classes.tableContainer}>
                    <PrimeReactTable
                        value={this.state.data}
                        columns={this.state.columns}
                        editMode="row"
                        frozenWidth={'80px'}
                        tableSelectionMode="checkbox"
                        uniqueKeyField={this.uniqueKeyField}
                        editingRows={this.state.editingRow}
                        paginator={this.state.data.length > 0}
                        currentPage={this.state.pageNum}
                        pageSize={this.state.pageSize}
                        onPage={this.onPage}
                        totalRecords={this.state.totalCount}
                        editor={(key, col, others) => {
                            return this.getRowEditor(key, col, others);
                        }}
                    />
                </div>
                <DrcDialog
                    title={'Add New Record'}
                    open={this.state.showAddNew}
                    buttons={
                        <>
                            <DrcButton
                                onClick={() => {
                                    this.setState({ showAddNew: false, newRecord: {} });
                                }}
                            >
                                <DrcTranslate>{'Cancel'}</DrcTranslate>
                            </DrcButton>
                            <DrcButton
                                isPrimary
                                onClick={this.addNewRecord}
                                disabled={
                                    !!this.state.invalidFields.length ||
                                    !(
                                        this.state.newRecord['CommissionRate'] &&
                                        this.state.newRecord['Commodity'] &&
                                        this.state.newRecord['CoolerName'] &&
                                        this.state.newRecord['StartDateActive']
                                    )
                                }
                            >
                                <DrcTranslate>{'Submit'}</DrcTranslate>
                            </DrcButton>
                        </>
                    }
                >
                    <Form>
                        <DrcSelect
                            label="Commodity"
                            value={this.state.newRecord['Commodity']}
                            required
                            options={commodityOptions}
                            validations={this.validationFields['Commodity'] ? this.validationFields['Commodity'].validations : []}
                            {...(this.validationFields['Commodity'] ? this.validationFields['Commodity'].customProps : {})}
                            onChange={(option) => this.setState((prevState) => ({ newRecord: { ...prevState.newRecord, Commodity: option } }))}
                        />
                        <GGSFormInput
                            label={'Location Code'}
                            validations={this.validationFields['LocationCode'] ? this.validationFields['LocationCode'].validations : []}
                            {...(this.validationFields['LocationCode'] ? this.validationFields['LocationCode'].customProps : {})}
                            onChange={(e) => this.setState((prevState) => ({ newRecord: { ...prevState.newRecord, LocationCode: e.target.value } }))}
                            value={this.state.newRecord['LocationCode']}
                            validationDetected={(validate) => this.handleValidationDetected(validate, 'LocationCode')}
                        />
                        <DrcSelect
                            label="Cooler Name"
                            value={this.state.newRecord['CoolerName']}
                            required
                            options={makeOptions(this.props.CoolerNameLookUp, 'LookupCode', 'LookupCode')}
                            validations={this.validationFields['CoolerName'] ? this.validationFields['CoolerName'].validations : []}
                            {...(this.validationFields['CoolerName'] ? this.validationFields['CoolerName'].customProps : {})}
                            onChange={(option) => this.setState((prevState) => ({ newRecord: { ...prevState.newRecord, CoolerName: option } }))}
                        />
                        <GGSFormInput
                            label={'Application'}
                            validations={this.validationFields['Application'] ? this.validationFields['Application'].validations : []}
                            {...(this.validationFields['Application'] ? this.validationFields['Application'].customProps : {})}
                            onChange={(e) => this.setState((prevState) => ({ newRecord: { ...prevState.newRecord, Application: e.target.value } }))}
                            value={this.state.newRecord['Application']}
                            validationDetected={(validate) => this.handleValidationDetected(validate, 'Application')}
                        />
                        <GGSFormInput
                            label={'Commission Rate'}
                            required
                            validations={this.validationFields['CommissionRate'] ? this.validationFields['CommissionRate'].validations : []}
                            {...(this.validationFields['CommissionRate'] ? this.validationFields['CommissionRate'].customProps : {})}
                            onChange={(e) =>
                                !this.exceedsTwoDecimals(e.target.value) &&
                                this.setState((prevState) => ({ newRecord: { ...prevState.newRecord, CommissionRate: e.target.value } }))
                            }
                            value={this.state.newRecord['CommissionRate']}
                            validationDetected={(validate) => this.handleValidationDetected(validate, 'CommissionRate')}
                        />

                        <DrcDatePicker
                            label={'Start Date Active'}
                            selectedDate={this.state.newRecord['StartDateActive'] || null}
                            required
                            InputProps={{ readOnly: true }}
                            onChange={(e) => this.setState((prevState) => ({ newRecord: { ...prevState.newRecord, StartDateActive: e } }))}
                        />
                        <DrcDatePicker
                            label={'End Date Active'}
                            selectedDate={this.state.newRecord['EndDateActive'] || null}
                            InputProps={{ readOnly: true }}
                            onChange={(e) => this.setState((prevState) => ({ newRecord: { ...prevState.newRecord, EndDateActive: e } }))}
                        />
                        <p className={classes.errorClass}>{this.state.dateError || ''}</p>
                    </Form>
                </DrcDialog>
            </DrcWrapper>
        );
    }
}
const mapStateToProps = ({ masterReducer, filterReducer, rootReducer }) => ({
    isMasterDataInitialized: masterReducer.isInitialized,
    userLang: masterReducer.userLang,
    pageTitle: rootReducer.pageTitle,
    filters: filterReducer.filters,
    isUserAdmin: rootReducer.userIsAdmin,
    CoolerNameLookUp: masterReducer[LOOKUP_FIELDS.CoolerNameLookUp],
    selectedRole: rootReducer.selectedRole
});

const mapDispatchToProps = (dispatch) => ({
    showToast: (message, type) => dispatch(showToast(message, type)),
    showLoadingScreenAction: (message) => dispatch(showLoadingScreenAction(message)),
    setPageTitle: (title) => dispatch(setPageTitleAction(title)),
    hideLoadingScreenAction: () => dispatch(hideLoadingScreenAction())
});

export default connect(mapStateToProps, mapDispatchToProps)(withOktaAuth(withStyles(styles)(SettlementRate)));
